import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Calendar, Edit2, RefreshCw, Square, MapPin } from "lucide-react";
import { ScheduleEditDialog } from "@/components/SiteAdminSchedule";
import TournamentLockCard from "@/components/admin/TournamentLockCard";
// iter87.180.9 — Swap window.confirm for BrutalConfirm on every async
// destructive path (iOS Safari silently drops native confirms).
import { useBrutalConfirm } from "@/components/BrutalConfirm";

const _PRETTY_ROUND_LABELS = {
    "GROUP": "Group", "GROUP STAGE": "Group", "REGULAR SEASON": "Group",
    "R64": "R64", "R32": "R32", "R16": "R16", "QF": "QF", "SF": "SF",
    "FINAL": "Final", "POSTSEASON": "Knockout", "3RD": "3rd-Place",
};
function _prettyRoundLabel(raw) {
    if (!raw) return null;
    const key = String(raw).toUpperCase();
    if (key.startsWith("STATUS_")) return null;
    return _PRETTY_ROUND_LABELS[key] || raw;
}

const _ESPN_CAPABLE_TEMPLATES = new Set(["world_cup", "world_cup_knockout", "march_madness", "football_playoffs"]);

export default function ScheduleTab({ tournament, leagueId, isLocked, coAdminCtx, sessionData, onChanged, onOpenComplete }) {
    const [scheduleEntries, setScheduleEntries] = useState([]);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [scheduleEditing, setScheduleEditing] = useState(null);
    const [scheduleRefreshing, setScheduleRefreshing] = useState(false);
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();

    const loadSchedule = useCallback(async () => {
        const tid = tournament?.id;
        if (!tid) return;
        setScheduleLoading(true);
        try {
            const r = await api.getSchedule(tid);
            setScheduleEntries(r.entries || []);
        } catch (e) {
            console.error("loadSchedule failed", e);
        }
        setScheduleLoading(false);
    }, [tournament?.id]);

    useEffect(() => {
        loadSchedule();
    }, [loadSchedule]);

    // iter60 — Auto-refresh every 75s when at least one entry is LIVE or
    // scheduled for TODAY. Mirrors the dashboard ScheduleWidget polling.
    // Pauses on hidden tabs.
    const hasLiveOrToday = useMemo(() => {
        const todayIso = new Date().toISOString().slice(0, 10);
        return (scheduleEntries || []).some(e => {
            if (e.status === "live") return true;
            if (!e.scheduled_at || e.played) return false;
            return (e.scheduled_at || "").slice(0, 10) === todayIso;
        });
    }, [scheduleEntries]);

    useEffect(() => {
        if (!hasLiveOrToday) return undefined;
        const id = setInterval(() => {
            if (typeof document !== "undefined" && document.hidden) return;
            loadSchedule();
        }, 75_000);
        return () => clearInterval(id);
    }, [hasLiveOrToday, loadSchedule]);

    const handleScheduleDelete = async (entry) => {
        if (!(await brutalConfirm({
            title: "Delete this schedule entry?",
            body: `${entry.home_label} vs ${entry.away_label}`,
            confirmLabel: "Delete",
            destructive: true,
        }))) return;
        try {
            // iter84.59.13 — Backend now returns real source provenance
            // (espn / api_football / manual / gotsport); the loose-fallback
            // sentinel `"match"` is the only one without a scheduled_matches
            // doc, so it's the only one that takes the match-delete branch.
            if (entry.source !== "match") {
                await api.leagueDeleteSchedule(leagueId, entry.id);
            } else {
                await api.deleteMatch(entry.match_id || entry.id);
            }
            toast.success("Schedule entry deleted");
            loadSchedule();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    // iter86.31.12 — Rivalry admins no longer trigger vendor syncs from
    // here: fixtures/scores auto-refresh via the platform pollers and the
    // cached vendor feed. (Manual refresh handlers removed — this tab is
    // a cached data view + local data-repair tools only.)

    // iter84.57.15 — Force-close a stuck-live match. Use case: ESPN /
    // API-Football fed the kickoff + live updates, but the upstream
    // never published the FT signal — the match has been "LIVE 73'"
    // for hours. The poller will keep refreshing the minute marker
    // forever. This button:
    //   1) confirms with the operator that they're forcing the row
    //      into "post" status,
    //   2) PATCHes the row with `{status: "post"}` (and the current
    //      live score, if both sides have one), which sets
    //      `is_manual_override=true` so the poller stops resurrecting
    //      the live state.
    //   3) if scores are present, `_edit_schedule_impl` runs them
    //      through `_apply_match` so leaderboards update.
    // The operator can still tweak the final score later via the
    // pencil ✏️ edit if upstream eventually publishes corrected stats.
    const handleForceClose = async (entry) => {
        const scoreA = entry.score_a;
        const scoreB = entry.score_b;
        const hasScore = scoreA !== null && scoreA !== undefined && scoreB !== null && scoreB !== undefined;
        const scoreText = hasScore ? `${scoreA}–${scoreB}` : "no score yet (you can edit it later)";
        const confirmBody = (
            `${entry.home_label} vs ${entry.away_label}\n`
            + `Final score: ${scoreText}\n\n`
            + `This sets the match to manual override — upstream sync (ESPN / API-Football) will stop overwriting it.`
        );
        if (!(await brutalConfirm({
            title: "Force this LIVE match to FINAL?",
            body: confirmBody,
            confirmLabel: "Force final",
            destructive: true,
        }))) return;
        try {
            const body = { status: "post" };
            if (hasScore) {
                body.score_a = scoreA;
                body.score_b = scoreB;
            }
            await api.leagueEditSchedule(leagueId, entry.id, body);
            toast.success(`Match closed${hasScore ? " — leaderboard updated" : ""}`);
            loadSchedule();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Force-close failed");
        }
    };

    // iter84 — Emergency-fix button for the duplicate-matches drift bug.
    // Walks every played match, removes any dup (same espn_id OR same
    // (stage, team pair) for manual games), and rebuilds team stats from
    // scratch. Safe + idempotent — running on a clean tournament is a no-op.
    const handleRecomputeStats = async () => {
        if (!(await brutalConfirm({
            title: "Recompute team stats?",
            body: (
                "Walks every played match for this tournament, removes any "
                + "duplicate match rows, and rebuilds every team's W/D/L/GF/GA "
                + "from scratch.\n\n"
                + "Use when a team is showing more games played than it has "
                + "actually played, or the leaderboard looks off after an ESPN "
                + "sync or a manual score edit.\n\n"
                + "Safe + idempotent — running on a clean tournament is a no-op."
            ),
            confirmLabel: "Recompute",
        }))) return;
        setScheduleRefreshing(true);
        try {
            const r = await api.recomputeTeamStats(tournament.id, sessionData?.participant_id);
            if (r.matches_deleted === 0) {
                toast.info("No duplicates found — tournament already clean.");
            } else {
                toast.success(
                    `Removed ${r.matches_deleted} duplicate match(es); rebuilt stats for ${r.teams_recomputed} team(s).`
                );
            }
            loadSchedule();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Recompute failed");
        }
        setScheduleRefreshing(false);
    };


    const handleRecomputeBracket = async () => {
        if (!(await brutalConfirm({
            title: "Recompute bracket?",
            body: (
                "This wipes any round labels polluted with raw ESPN status "
                + "strings (STATUS_FINAL, STATUS_IN_PROGRESS, etc.) so the "
                + "schedule view shows the proper round labels again.\n\n"
                + "iter84.59.3 — the date-window demoter was removed from this "
                + "endpoint. Use the per-row Override dropdown or Lock-all-labels "
                + "to manage round labels, and the Site Admin 'Recompute Team Stats' "
                + "button to rebuild the leaderboard from the matches collection.\n\n"
                + "Manually-pinned rows are protected. Safe to run anytime."
            ),
            confirmLabel: "Recompute",
        }))) return;
        setScheduleRefreshing(true);
        try {
            const r = await api.recomputeBracket(tournament.id, sessionData?.participant_id);
            if (r.skipped) {
                toast.info(r.reason || "Nothing to do.");
            } else {
                toast.success(
                    `Bracket cleanup complete — ${r.status_labels_cleaned || 0} polluted status label(s) cleared.`
                );
            }
            loadSchedule();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Recompute failed");
        }
        setScheduleRefreshing(false);
    };

    return (
        <div className="space-y-4" data-testid="admin-schedule-tab-wrapper">
            <TournamentLockCard isLocked={isLocked} coAdminCtx={coAdminCtx} onOpenComplete={onOpenComplete} />
            <div className="brutal-card p-5 space-y-4" data-testid="admin-schedule-tab">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h2 className="font-display font-extrabold text-xl uppercase flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-cyber" /> Schedule
                        </h2>
                        <p className="text-xs text-zinc-400 mt-1">
                            Past, live, and upcoming games. Add games manually or fix incorrect scores — connected feeds refresh automatically.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        {(tournament.api_football_league_id || (tournament.espn_sport && tournament.espn_league) || tournament.gotsport_event_id) && (
                            <span
                                data-testid="admin-schedule-autosync-chip"
                                title="Fixtures and scores refresh automatically from the cached vendor feed — no manual sync needed."
                                className="pill bg-neon/10 border-neon text-neon text-[10px] font-bold px-2.5 py-1 flex items-center gap-1.5"
                            >
                                <RefreshCw className="w-3 h-3" /> Auto-synced · cached feed
                            </span>
                        )}
                        {_ESPN_CAPABLE_TEMPLATES.has(tournament.template) && (
                            <button
                                data-testid="admin-schedule-recompute-bracket-btn"
                                onClick={handleRecomputeBracket}
                                disabled={scheduleRefreshing || isLocked}
                                title="Fix any group games mis-stamped as KO rounds in your bracket."
                                className="brutal-btn text-xs py-2 bg-zinc-800 text-white border-black shadow-brutal disabled:opacity-40"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${scheduleRefreshing ? "animate-spin" : ""}`} />
                                Recompute bracket
                            </button>
                        )}
                        {/* iter84 — Recompute team stats: dedupes the matches collection AND
                            rebuilds every team's W/D/L/GF/GA from scratch. Use when a team
                            shows more games played than physically possible (the Morocco
                            P=4 production bug fix). Safe + idempotent. */}
                        <button
                            data-testid="admin-schedule-recompute-stats-btn"
                            onClick={handleRecomputeStats}
                            disabled={scheduleRefreshing || isLocked}
                            title="Dedupe matches and rebuild team W/D/L from scratch. Use if a team shows the wrong number of games played."
                            className="brutal-btn text-xs py-2 bg-zinc-800 text-white border-black shadow-brutal disabled:opacity-40"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${scheduleRefreshing ? "animate-spin" : ""}`} />
                            Recompute team stats
                        </button>
                        <button
                            data-testid="admin-schedule-add-btn"
                            onClick={() => setScheduleEditing("new")}
                            disabled={isLocked}
                            className="brutal-btn text-xs py-2 bg-blaze text-white border-black shadow-brutal disabled:opacity-40"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add game
                        </button>
                    </div>
                </div>

                {(tournament.espn_last_synced_at || tournament.gotsport_last_synced_at || tournament.api_football_last_synced_at) && (() => {
                    const apif = tournament.api_football_last_synced_at;
                    const espn = tournament.espn_last_synced_at;
                    const gs = tournament.gotsport_last_synced_at;
                    // Pick the freshest of the available sources.
                    const candidates = [
                        apif && { ts: apif, name: "API-Football" },
                        espn && { ts: espn, name: "ESPN" },
                        gs && { ts: gs, name: "GotSport" },
                    ].filter(Boolean);
                    candidates.sort((a, b) => (b.ts > a.ts ? 1 : -1));
                    const top = candidates[0];
                    return top ? (
                        <p className="text-[10px] text-zinc-500 -mt-2" data-testid="admin-schedule-last-synced">
                            Last synced from <span className="text-cyber font-bold">{top.name}</span> · {new Date(top.ts).toLocaleString()}
                        </p>
                    ) : null;
                })()}

                {/* iter63o — API-Football publishes KO fixtures only AFTER the
                    group stage finishes. When the tournament has KO stages
                    declared but `scheduled_matches` has nothing past group
                    stage, set expectations rather than leaving the user to
                    wonder why R32/R16/QF/SF/Final are missing. */}
                {(() => {
                    const isApifTpl = tournament.template === "world_cup_api" || tournament.template === "world_cup_ko_api" || !!tournament.api_football_league_id;
                    if (!isApifTpl) return null;
                    if (scheduleLoading || scheduleEntries.length === 0) return null;
                    const koStageKeys = new Set(["r64", "r32", "r16", "qf", "sf", "final", "champion"]);
                    const tournamentHasKoStages = (tournament.stages || []).some(s => koStageKeys.has(s.key));
                    if (!tournamentHasKoStages) return null;
                    const hasKoRows = scheduleEntries.some(e => {
                        const raw = (e.stage_key || e.stage || e.round_label || "").toString().toLowerCase();
                        return koStageKeys.has(raw) || /\b(r\s*32|r\s*16|round\s*of|quarter|semi|final)\b/i.test(raw);
                    });
                    if (hasKoRows) return null;
                    return (
                        <div
                            data-testid="admin-schedule-ko-pending-banner"
                            className="brutal-card-light p-3 text-xs flex items-start gap-2 border-cyber/40"
                        >
                            <Calendar className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                            <p className="text-zinc-300 leading-relaxed">
                                <span className="text-cyber font-bold uppercase tracking-wider">Knockout fixtures pending</span>{" "}
                                — API-Football typically publishes Round of 32 → Final
                                only after the group stage concludes. The bracket already shows the
                                full structure with TBDs; the rows here will populate on the next
                                refresh once upstream releases them.
                            </p>
                        </div>
                    );
                })()}

                {scheduleLoading && <p className="text-xs text-zinc-500">Loading…</p>}
                {!scheduleLoading && scheduleEntries.length === 0 && (
                    <p className="text-xs text-zinc-500" data-testid="admin-schedule-empty">
                        No games yet. Click <span className="text-blaze">Add game</span> to enter one
                        {tournament.api_football_league_id ? <> or <span className="text-volt">Refresh from API-Football</span> to pull fixtures</>
                            : tournament.espn_sport && tournament.espn_league ? <> or <span className="text-neon">Refresh from ESPN</span> to pull fixtures</>
                            : null}.
                    </p>
                )}

                {!scheduleLoading && scheduleEntries.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="admin-schedule-table">
                            <thead>
                                <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-wider">
                                    <th className="p-2">When</th>
                                    <th className="p-2">Round</th>
                                    <th className="p-2">Home</th>
                                    <th className="p-2 text-center">Score</th>
                                    <th className="p-2">Away</th>
                                    <th className="p-2">Status</th>
                                    <th className="p-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scheduleEntries.map(e => (
                                    <tr key={e.id} className="border-t border-zinc-800 hover:bg-zinc-900/50" data-testid={`admin-schedule-row-${e.id}`}>
                                        <td className="p-2 whitespace-nowrap">
                                            <div>{e.scheduled_at ? new Date(e.scheduled_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}</div>
                                            {/* iter87.177 — Venue line for GotSport games. */}
                                            {e.location && (
                                                <a
                                                    href={e.location_url
                                                        || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.location)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`admin-schedule-location-${e.id}`}
                                                    className="mt-0.5 flex items-center gap-1 text-[10px] text-zinc-500 hover:text-cyber transition-colors max-w-[180px]"
                                                    title={`Open ${e.location} in Maps`}
                                                >
                                                    <MapPin className="w-2.5 h-2.5 shrink-0" />
                                                    <span className="truncate">{e.location}</span>
                                                </a>
                                            )}
                                        </td>
                                        <td className="p-2">{_prettyRoundLabel(e.round_label) || "—"}</td>
                                        <td className={`p-2 ${e.home_is_tbd ? "italic text-zinc-500" : ""}`}>{e.home_label}</td>
                                        <td className="p-2 text-center font-mono">
                                            {e.played ? `${e.score_a}–${e.score_b}` : "—"}
                                        </td>
                                        <td className={`p-2 ${e.away_is_tbd ? "italic text-zinc-500" : ""}`}>{e.away_label}</td>
                                        <td className="p-2 text-[10px] uppercase">
                                            {(() => {
                                                if (e.status === "live") return (
                                                    <span className="text-neon" data-testid={`admin-schedule-status-live-${e.id}`}>
                                                        ● LIVE
                                                        {e.live_detail && (
                                                            <span className="ml-1 normal-case text-zinc-300">· {e.live_detail}</span>
                                                        )}
                                                    </span>
                                                );
                                                if (e.played) return <span className="text-zinc-500">FINAL</span>;
                                                if (e.status === "cancelled") return <span className="text-blaze">CANCELLED</span>;
                                                return <span className="text-zinc-600">SCHEDULED</span>;
                                            })()}
                                            {e.is_manual_override && <span className="ml-1 text-blaze">·M</span>}
                                        </td>
                                        <td className="p-2 text-right whitespace-nowrap">
                                            {e.status === "live" && (
                                                <button
                                                    data-testid={`admin-schedule-force-close-${e.id}-btn`}
                                                    onClick={() => handleForceClose(e)}
                                                    disabled={isLocked}
                                                    className="brutal-btn-ghost text-xs p-1.5 text-neon disabled:opacity-30"
                                                    title="Force close this stuck-LIVE match (upstream isn't publishing the FT signal)"
                                                >
                                                    <Square className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                            <button
                                                data-testid={`admin-schedule-edit-${e.id}-btn`}
                                                onClick={() => setScheduleEditing(e)}
                                                disabled={isLocked}
                                                className="brutal-btn-ghost text-xs p-1.5 disabled:opacity-30"
                                                title="Edit"
                                            >
                                                <Edit2 className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                data-testid={`admin-schedule-delete-${e.id}-btn`}
                                                onClick={() => handleScheduleDelete(e)}
                                                disabled={isLocked}
                                                className="brutal-btn-ghost text-xs p-1.5 text-blaze disabled:opacity-30"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {scheduleEditing && (
                    <ScheduleEditDialog
                        entry={scheduleEditing === "new" ? null : scheduleEditing}
                        tournament={tournament}
                        tournamentId={tournament.id}
                        leagueId={leagueId}
                        apiMode="league-admin"
                        onClose={() => setScheduleEditing(null)}
                        onSaved={() => { setScheduleEditing(null); loadSchedule(); onChanged?.(); }}
                    />
                )}
            </div>
            {confirmHost}
        </div>
    );
}
